<template>
  <div>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-toolbar color="accent">
            <v-toolbar-title style="width: 100%">
              <div class="float-left pt-2">
                <h6>
                  <v-icon color="primary" class="mt-n1">
                    mdi-map-marker-radius
                  </v-icon>
                  <span class="mt-n2"> {{ title }}</span>
                </h6>
              </div>
            </v-toolbar-title>
          </v-toolbar>

          <v-card-text class="pb-0">
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  :placeholder="$vuetify.lang.t('$vuetify.input.search')"
                  clearable
                  hide-details
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="3">
                <v-select
                  v-model="selectedType"
                  :items="glnTypes"
                  item-text="text"
                  item-value="value"
                  :label="$vuetify.lang.t('$vuetify.gln.type')"
                  clearable
                  hide-details
                ></v-select>
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  v-model="dateFrom"
                  type="date"
                  :label="$vuetify.lang.t('$vuetify.tables.date_from')"
                  clearable
                  hide-details
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  v-model="dateTo"
                  type="date"
                  :label="$vuetify.lang.t('$vuetify.tables.date_to')"
                  clearable
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>

          <v-data-table
            :headers="headers"
            :items="items"
            :options.sync="options"
            :server-items-length="totalItems"
            :footer-props="footerProps"
            :items-per-page="20"
            :loading="isLoading"
          >
            <template v-slot:item.company="{ item }">
              <router-link
                v-if="item.company && item.company.id"
                :to="'/manager/company/' + item.company.id"
              >
                {{ companyName(item.company) }}
              </router-link>
              <span v-else>-</span>
            </template>

            <template v-slot:item.name="{ item }">
              {{ localizedName(item.name) }}
            </template>

            <template v-slot:item.gln_type="{ item }">
              {{ glnTypeText(item.gln_type) }}
            </template>

            <template v-slot:item.status="{ item }">
              {{ statusText(item.status) }}
            </template>

            <template v-slot:item.insert_date="{ item }">
              {{ item.insert_date || "-" }}
            </template>

            <template v-slot:item.actions="{ item }">
              <GlnActions
                :gln="item"
                :loadingId="actionLoadingId"
                @edit="openEdit(item)"
                @remove="openDelete(item)"
              />
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <AddGln
      :popupAddGln="dialogEditGln"
      :orgID="selectedGln ? selectedGln.company_id : null"
      :glnData="selectedGln"
      @closePopupAddGln="closeEditDialog"
      @updated="handleUpdated"
    />

    <RemoveGln
      :removeGlnPopup="dialogRemoveGln"
      :gln="selectedGln"
      @closeRemoveGlnPopup="closeRemoveDialog"
      @reload="handleDeleted"
    />
  </div>
</template>

<script>
import GlnActions from "@/components/manager/GlnActions";
import AddGln from "@/components/manager/popup/AddGln";
import RemoveGln from "@/components/manager/popup/RemoveGln";
import { filterVisibleGlns } from "@/helpers/gln";
import Api from "@/services/api";

export default {
  components: {
    GlnActions,
    AddGln,
    RemoveGln,
  },
  data: function() {
    return {
      search: "",
      selectedType: null,
      dateFrom: "",
      dateTo: "",
      items: [],
      totalItems: 0,
      isLoading: false,
      searchTimer: null,
      actionLoadingId: null,
      dialogEditGln: false,
      dialogRemoveGln: false,
      selectedGln: null,
      footerProps: {
        "items-per-page-options": [20, 50, 100],
      },
      options: {
        page: 1,
        itemsPerPage: 20,
        sortBy: ["insert_date"],
        sortDesc: [true],
      },
    };
  },
  computed: {
    title() {
      return this.$vuetify.lang.t("$vuetify.cards.gln_list");
    },
    glnTypes() {
      return this.$vuetify.lang.t("$vuetify.gln.types");
    },
    headers() {
      return [
        {
          text: this.$vuetify.lang.t("$vuetify.tables.company"),
          value: "company",
          sortable: false,
        },
        {
          text: this.$vuetify.lang.t("$vuetify.company.gln"),
          value: "gln",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.tables.name"),
          value: "name",
          sortable: false,
        },
        {
          text: this.$vuetify.lang.t("$vuetify.gln.type"),
          value: "gln_type",
          sortable: false,
        },
        {
          text: this.$vuetify.lang.t("$vuetify.tables.status"),
          value: "status",
          sortable: false,
        },
        {
          text: this.$vuetify.lang.t("$vuetify.tables.date"),
          value: "insert_date",
        },
        {
          text: "",
          value: "actions",
          sortable: false,
        },
      ];
    },
  },
  watch: {
    options: {
      handler() {
        this.loadItems();
      },
      deep: true,
    },
    search(newVal) {
      if (newVal == null) {
        this.search = "";
        return;
      }

      this.queueReload();
    },
    selectedType() {
      this.reloadFromFirstPage();
    },
    dateFrom(newVal) {
      if (newVal == null) {
        this.dateFrom = "";
        return;
      }

      this.reloadFromFirstPage();
    },
    dateTo(newVal) {
      if (newVal == null) {
        this.dateTo = "";
        return;
      }

      this.reloadFromFirstPage();
    },
  },
  mounted() {
    this.loadItems();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    queueReload() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.reloadFromFirstPage();
      }, 300);
    },
    reloadFromFirstPage() {
      if ((this.options.page || 1) !== 1) {
        this.options = Object.assign({}, this.options, {
          page: 1,
        });
        return;
      }

      this.loadItems();
    },
    loadItems() {
      const query = new URLSearchParams();
      const page = this.options.page || 1;
      const itemsPerPage = this.options.itemsPerPage || 20;
      const sortBy = Array.isArray(this.options.sortBy)
        ? this.options.sortBy[0]
        : this.options.sortBy;
      const sortDesc = Array.isArray(this.options.sortDesc)
        ? this.options.sortDesc[0]
        : this.options.sortDesc;

      query.set("page", page);
      query.set("itemsPerPage", itemsPerPage);

      if (sortBy) {
        query.set("sortBy", sortBy);
      }

      if (typeof sortDesc !== "undefined") {
        query.set("sortDesc", sortDesc ? "true" : "false");
      }

      if (this.search && this.search.trim()) {
        query.set("searchVal", this.search.trim());
      }

      if (this.selectedType) {
        query.set("gln_type", this.selectedType);
      }

      if (this.dateFrom) {
        query.set("dateFrom", this.dateFrom);
      }

      if (this.dateTo) {
        query.set("dateTo", this.dateTo);
      }

      this.isLoading = true;
      Api.gln("list", query.toString())
        .then(({ data }) => {
          this.items = filterVisibleGlns(data.items);
          this.totalItems = data.total || 0;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    openEdit(item) {
      this.actionLoadingId = item.id;
      Api.gln("get", item.id)
        .then(({ data }) => {
          this.selectedGln = data;
          this.dialogEditGln = true;
        })
        .finally(() => {
          this.actionLoadingId = null;
        });
    },
    openDelete(item) {
      this.selectedGln = item;
      this.dialogRemoveGln = true;
    },
    closeEditDialog() {
      this.dialogEditGln = false;
      this.selectedGln = null;
    },
    closeRemoveDialog() {
      this.dialogRemoveGln = false;
      this.selectedGln = null;
    },
    handleUpdated() {
      this.loadItems();
    },
    handleDeleted() {
      const shouldGoBackOnePage =
        this.items.length === 1 && (this.options.page || 1) > 1;

      if (shouldGoBackOnePage) {
        this.options = Object.assign({}, this.options, {
          page: this.options.page - 1,
        });
        return;
      }

      this.loadItems();
    },
    localizedName(name) {
      if (!name) {
        return "-";
      }

      if (typeof name === "string") {
        return name;
      }

      return name[this.$i18n.locale] || name.en || name.ka || "-";
    },
    companyName(company) {
      if (!company || !company.name) {
        return "-";
      }

      if (typeof company.name === "string") {
        return company.name;
      }

      return (
        company.name[this.$i18n.locale] || company.name.en || company.name.ka || "-"
      );
    },
    glnTypeText(type) {
      const item = this.glnTypes.find((option) => option.value === type);

      return item ? item.text : type || "-";
    },
    statusText(status) {
      if (!status) {
        return "-";
      }

      return this.$i18n.messages[this.$i18n.locale].$vuetify.status[status] || status;
    },
  },
};
</script>
